<script setup lang="ts">
    import { computed, inject, onMounted, reactive, Ref, ref, watch } from 'vue';
    import axios from 'axios';
    import moment from 'moment';
    import { formatAmount, formatDate } from '@/utils';
    import { useToastStore } from '@/stores/toast.store';
    import { useConfirmationStore } from '@/stores/confirmation.store';

    const toastStore = useToastStore();
    const confirmationStore = useConfirmationStore();
    const startupInfos = reactive<{ salaires: any[] }>({ salaires: [] });
    const loading = ref(false);
    const loadingActions = ref(false);
    const collectivite = inject<Ref<any>>('collectivite');
    const itemsList = ref<any[]>([]);
    const filteredDatas = ref([] as Array<any>);
    const paginatedFilteredDatas = ref([] as Array<any>);
    const queryParams = reactive({
        pageNumber: 1
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const filtersParams = reactive({
        name: '',
        statut_professionnel: null,
        salaires: null,
        quotite_cessible_min: null,
        quotite_cessible_max: null,
        age_min: null,
        age_max: null,
        sexe: null,
        fonction: null
    });
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return paginatedFilteredDatas.value.length > 0 && selectedItems.value.filter(i => paginatedFilteredDatas.value.findIndex(p => p.id === i) !== -1).length === paginatedFilteredDatas.value.length;
    });
    const areAllItemsSelectedValidable = computed(() => {
        return selectedItems.value.length > 0 && itemsList.value.filter(i => selectedItems.value.includes(i.id) && i.statut_prospect == 0).length === selectedItems.value.length;
    });
    const showAdvancedFilters = ref(false);

    onMounted(() => {
        fetchUnverifiedMembres();
    });

    watch(filtersParams, (oldValues, newValues) => {
        applyFilters();
        onPaginate(1);
    });

    async function fetchUnverifiedMembres(withoutLoading?: boolean) {
        try {
            if (!withoutLoading) { loading.value = true; }
            const response = await axios.get(`/backoffice/collectivites/${collectivite?.value.id}/unverified_membres`);
            itemsList.value = response.data;
            applyFilters();
            onPaginate(1);
        } catch(error: any) {
            toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
        } finally {
            loading.value = false;
        }
    }

    async function sendInvitations(membreIds: any[]) {
        const confirmation = await confirmationStore.open('Validation des prospects', `Êtes-vous sûre de vouloir envoyer ${membreIds.length > 1 ? `des invitations aux ${membreIds.length} membres sélectionnés` : `une invitation au membre sélectionné`} ?`);
        if (!confirmation) return;

        try {
            loadingActions.value = true;
            await axios.post(`/backoffice/collectivites/${collectivite?.value.id}/unverified_membres/send-invitation`, { ids: membreIds });
            selectedItems.value = [];
            fetchUnverifiedMembres(true);
            toastStore.success("Invitations envoyées avec succès.");
        } catch(error: any) {
            toastStore.error(error.message || 'Une erreur est survenue lors de la requête au serveur.');
        } finally {
            loadingActions.value = false;
        }
    }

    function applyFilters() {
        let allDatas = itemsList.value;

        if (filtersParams.name) {
            allDatas = allDatas.filter(a => `${a.nom} ${a.prenom}`.toLowerCase().includes(filtersParams.name.toLowerCase()));
        }
        if (filtersParams.statut_professionnel) {
            if (filtersParams.statut_professionnel == '1') {
                allDatas = allDatas.filter(a => a.retraite === false);
            } else if (filtersParams.statut_professionnel == '2') {
                allDatas = allDatas.filter(a => a.retraite === true);
            }
        }
        if (filtersParams.sexe) {
            allDatas = allDatas.filter(a => a.sexe == filtersParams.sexe);
        }
        if (filtersParams.quotite_cessible_min && !isNaN(filtersParams.quotite_cessible_min)) {
            const quotite_cessible_min = parseFloat(filtersParams.quotite_cessible_min);
            allDatas = allDatas.filter(a => a.quotite_cessible_montant && !isNaN(a.quotite_cessible_montant) && a.quotite_cessible_montant >= quotite_cessible_min);
        }
        if (filtersParams.quotite_cessible_max && !isNaN(filtersParams.quotite_cessible_max)) {
            const quotite_cessible_max = parseFloat(filtersParams.quotite_cessible_max);
            allDatas = allDatas.filter(a => a.quotite_cessible_montant && !isNaN(a.quotite_cessible_montant) && a.quotite_cessible_montant <= quotite_cessible_max);
        }
        if (filtersParams.age_min) {
            const age_min = moment(filtersParams.age_min);
            if (age_min.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_naissance = moment(a.date_naissance);
                    return date_naissance.isValid() && date_naissance.isSameOrAfter(age_min);
                })
            }
        }
        if (filtersParams.age_max) {
            const age_max = moment(filtersParams.age_max);
            if (age_max.isValid()) {
                allDatas = allDatas.filter(a => {
                    const date_naissance = moment(a.date_naissance);
                    return date_naissance.isValid() && date_naissance.isSameOrBefore(age_max);
                })
            }
        }
        /*if (filtersParams.salaires) {
            const matchSalaire = startupInfos.salaires[filtersParams.salaires];
            if (matchSalaire) {
                allDatas = allDatas.filter(a => !isNaN(a.salaire) && parseFloat(a.salaire) >= parseFloat(matchSalaire.salaire_min) && parseFloat(a.salaire) <= parseFloat(matchSalaire.salaire_max));
            }
        }*/
        
        filteredDatas.value = allDatas;
    }

    function onPaginate(page: number) {
        queryParams.pageNumber = page;
        pagination.total = filteredDatas.value.length;
        pagination.last_page = Math.ceil(pagination.total / pagination.per_page);
        const start = (page - 1) * pagination.per_page;
        paginatedFilteredDatas.value =  filteredDatas.value.slice(start, start + pagination.per_page);
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = [...selectedItems.value.filter(s => itemsList.value.findIndex(p => p.id === s) === -1), ...itemsList.value.map(i => i.id)];
        } else {
            selectedItems.value = [];
        }
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }
</script>
<template>
    <section class="s20_mobile px-4">
        <div class="widget px-7 py-7">
            
            <div class="s7_mobile container-full-2">
                <h2 class="flex align-items-center gap-4 font-medium text-primary-color my-5">
                    <img src="/icons/icon-info-perso.svg" class="size-l" alt="" />
                    Invitations
                </h2>
            </div> 
            <div class="s13 flex justify-content-right">
                <template v-if="selectedItems.length > 0">
                    <custom-button type="button" :classesList="'btn white2 active'" @click="sendInvitations(selectedItems)" :loading="loadingActions">
                        <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                        <span>Inviter ({{ selectedItems.length }})</span>
                    </custom-button>
                </template>
                <custom-button type="button" :classesList="'btn light'" @click="fetchUnverifiedMembres">
                    <img src="/icons/icon-list.svg" class="btn-icon" alt="" />
                    <span>Actualiser</span>
                </custom-button>
                <button class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter" @click="toggleAdvancedFilters">
                    <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                    <span>Options</span>
                </button>
            </div>
            <div class="clear-both"></div>

            <div class="s20 items-center gap-6 px-0 pb-6 collapse-content active">
                <!-- Filtre - ajouter -->
                <div class="custom-filter-set-area my-5" v-if="showAdvancedFilters">
                    <div class="grid-filter6 mt-4 mb-0">
                        <div class="form-field-floating reactive sm">
                            <input type="text" class="form-input" v-model="filtersParams.name" name="name" id="name" placeholder="">
                            <label for="name">Nom, prénoms</label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.statut_professionnel" name="statut_professionnel" id="statut_professionnel">
                                <option :value="null">Tous les statuts</option>
                                <option value="1">Actifs</option>
                                <option value="2">Retraités</option>
                            </select>
                            <label for="statut_professionnel">Statut <b class="color-danger">*</b></label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.salaires" name="salaires" id="salaires">
                                <option :value="null">Tous les salaires</option>
                                <option :value="index" v-for="(salaire, index) of startupInfos.salaires" :key="index">{{ formatAmount(salaire.salaire_min) + ' - ' + formatAmount(salaire.salaire_max) }}</option>
                            </select>
                            <label for="salaires">Salaires <b class="color-danger">*</b></label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="number" class="form-input" v-model="filtersParams.quotite_cessible_min" name="quotite_cessible_min" id="quotite_cessible_min" placeholder="">
                            <label for="quotite_cessible_min">Quotité cessible &gt;=</label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="number" class="form-input" v-model="filtersParams.quotite_cessible_max" name="quotite_cessible_max" id="quotite_cessible_max" placeholder="">
                            <label for="quotite_cessible_max">Quotité cessible &gt;=</label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="date" class="form-input" v-model="filtersParams.age_min" name="age_min" id="age_min" placeholder="">
                            <label for="age_min">Age &gt;=</label>
                        </div>
                        <div class="form-field-floating reactive sm">
                            <input type="date" class="form-input" v-model="filtersParams.age_max" name="age_max" id="age_max" placeholder="">
                            <label for="age_max">Age &lt;=</label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.sexe" name="sexe" id="sexe">
                                <option :value="null">Tous les sexes</option>
                                <option value="M">Masculin</option>
                                <option value="F">Féminin</option>
                            </select>
                            <label for="sexe">Sexe <b class="color-danger">*</b></label>
                        </div>
                        <div class="form-field-floating sm">
                            <select class="form-input-select" v-model="filtersParams.fonction" name="fonction" id="fonction">
                                <option :value="null">Toutes les fonctions</option>
                            </select>
                            <label for="fonction">Fonction <b class="color-danger">*</b></label>
                        </div>
                    </div>
                </div>
                <!---->
                <table class="table separator skin3 selection rounded"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                    <thead>
                        <tr>
                            <th>
                                <label class="btn-circle" for="check-list1">
                                    <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                </label>
                            </th>
                            <th></th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Information</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Statut</th>
                            <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Date dernière invitation</th>
                            <th class="flex-right">
                                
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="5" class="text-center py-5">
                                    <div style="display: flex; justify-content: center" v-if="loading">
                                        <div class="loader dark"></div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="paginatedFilteredDatas.length === 0">
                            <tr>
                                <td colspan="5" class="text-center py-5">
                                    Aucun membre trouvé
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr class="text-grey-700 over-effect over-effect-scale" v-for="(item, index) of paginatedFilteredDatas" :key="index"><!-- over-effect active-->
                                <td class="selection-item">
                                    <label class="btn-circle" :for="'item_check_' + index">
                                        <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                    </label>
                                </td>
                                <td>
                                    <!-- <div class="square-lg px-3 py-3" style="background-color: rgb(var(--color-success-rgb), 0.2);">
                                        <img src="/icons/icon-menu-carte.svg" width="100%" />
                                    </div> -->
                                    <div class="square-xl rounded-15" style="background-color: rgb(var(--color-warning-rgb), 0.2);"><img :src="'/assets/users_avatar/600w/user.jpg'" width="100%" /></div>
                                </td>
                                <td>
                                    <p class="text-xl font-semibold mb-2">
                                        {{ item.nom + ' ' + item.prenom }} &nbsp; 
                                        <span v-if="item.actif == 1" class="tag-state notif stroke">Actif</span>
                                        <span v-if="item.actif == 0" class="tag-state danger stroke">Inactif</span>
                                    </p>
                                    <p class="text-sm text-grey-600">{{ item.email }}</p>
                                    <p class="text-sm text-grey-600">{{ item.telephone }}</p>
                                </td>
                                <td>
                                    <span v-if="!item.email_verified_at" class="tag-state warning stroke">En attente</span>
                                    <span v-if="item.email_verified_at" class="tag-state success stroke">Vérifié</span>
                                </td>
                                <td>{{ item.last_date_invitation ? formatDate(item.last_date_invitation) : '-' }}</td>
                                <td>
                                    <div class="flex-right">
                                        <custom-button type="button" :classesList="'btn white2 active'" @click="sendInvitations([item.id])" :loading="loadingActions" v-if="collectivite?.verrouille">
                                            <img src="/icons/icon-send.svg" class="btn-icon" alt="" />
                                            <span>Inviter</span>
                                        </custom-button>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
                <!--Pagination-->
                <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                <!---->
            </div>

            <div class="clear-both"></div>
        </div>
    </section>
</template>